import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { autorun } from 'mobx';

import moment from 'moment';
import ExternalCoachContext from '../ExternalCoachContext';
import NavigationContext, {
  NavigationRouteType,
} from '../NavigationContext';
import UserContext from '../../../context/UserContext';
import useComponentMounted from '../../../hooks/useComponentMounted';
import { usePodFilter } from '../../../context/PodContext/usePodFilter';
import { isRemovePublicRecipesEnabled } from '../../../utils/postHog';
import Recipe from '../../Model/Recipe';
import CoachConfig from '../../Model/CoachConfig';
import { RefreshInterval } from '../../Model/MealPlanAssignment';
import ClientMealAssignmentView from '../../Model/ClientMealAssignmentView';
import {
  DEFAULT_MEAL_REFRESH_DAYS,
  DaysForRefreshInterval,
  MealPlanAssignmentStatus,
  isPlanLive,
} from '../../utils/mealPlan';
import MealPlanView from '../../Model/MealPlanView';
import Ingredient from '../../Model/Ingredient';
import MealPlanContext, { initialValues } from './MealPlanContext';

const MealPlanContextProvider = ({
  children,
}) => {
  const [isReady, setIsReady] = useState(initialValues.isReady);
  const [isLoading, setIsLoading] = useState(initialValues.isLoading);
  const [publicRecipesCollection, setPublicRecipesCollection] = useState(initialValues.publicRecipesCollection);
  const [coachRecipesCollection, setCoachRecipesCollection] = useState(initialValues.coachRecipesCollection);
  const [recipesCollection, setRecipesCollection] = useState(initialValues.recipesCollection);
  const [recipesDocs, setRecipesDocs] = useState(initialValues.recipesDocs);
  const [mealPlanAssignments, setMealPlanAssignments] = useState(initialValues.mealPlanAssignments);
  const [automationTemplate, setAutomationTemplate] = useState(initialValues.automationTemplate);
  const [filteredMealPlanAssignments, setFilteredMealPlanAssignments] = useState(initialValues.mealPlanAssignments);
  const [assignmentViewsCollection, setAssignmentViewsCollection] = useState(initialValues.assignmentViewsCollection);
  const [coachMealPlanViewsCollection, setCoachMealPlanViewsCollection] = useState(
    initialValues.coachMealPlanViewsCollection,
  );
  const [publicMealPlanViewsCollection, setPublicMealPlanViewsCollection] = useState(
    initialValues.publicMealPlanViewsCollection,
  );
  const [comnbinedMealPlanViews, setComnbinedMealPlanViews] = useState([]);
  const [mealPlansStatusCount, setMealPlansStatusCount] = useState(initialValues.mealPlansStatusCount);
  const [showArchivedMealPlans, setShowArchivedMealPlans] = useState(initialValues.showArchivedMealPlans);
  const [showArchivedRecipes, setShowArchivedRecipes] = useState(initialValues.showArchivedRecipes);
  const [isArchivedRecipesLoaded, setIsArchivedRecipesLoaded] = useState(false);
  const [mealPlanConfig, setMealPlanConfig] = useState(initialValues.mealPlanConfig);
  const [areCollectionsReady, setAreCollectionsReady] = useState(false);
  const [coachConfig, setCoachConfig] = useState(initialValues.coachConfig);
  const [alwaysIncludePublicRecipesDocs, setAlwaysIncludePublicRecipesDocs] = useState([]);
  const [canonicalIngredients, setCanonicalIngredients] = useState(initialValues.canonicalIngredients);
  const [isCanonicalIngredientsLoading, setIsCanonicalIngredientsLoading] = useState(
    initialValues.isCanonicalIngredientsLoading,
  );

  // TODO: Move this to a separate context. Maybe MealPlanEditorContext, or to the container component directly.
  const [selectedRecipe, setSelectedRecipe] = useState(initialValues.selectedRecipe);

  const { getFilteredDocs } = usePodFilter();

  const {
    isAdminGroupRoute,
    routeType,
  } = useContext(NavigationContext);

  const {
    userId: coachId,
    userDoc: coachUserDoc,
  } = useContext(UserContext);

  /*
    Root read on purpose: this is the COACH'S OWN user doc, which carries no
    coachData entries (coach-own-doc state is out of the sub-properties
    migration's scope, like the try-challenge sandbox). The entry-backed
    getter would always return '' here; saveDefaultMealPlan below writes the
    same root field. The getter remains the right read for CLIENT docs.
  */
  const defaultMealPlan = coachUserDoc?.data?.defaultMealPlan;

  const {
    coachDoc = {},
  } = useContext(ExternalCoachContext);

  useEffect(() => {
    const disposer = autorun(() => {
      if (coachDoc) {
        setMealPlanConfig(coachDoc?.mealPlanConfig);
      }
    });
    return disposer;
  }, [coachDoc]);

  const isComponentMountedRef = useComponentMounted();

  const shouldUsePublicRecipes = useMemo(() => (
    !isRemovePublicRecipesEnabled(coachId)
  ), [coachId]);

  // When public recipes are disabled for this coach, optionally fetch a small allowlist of public recipes by id.
  useEffect(() => {
    if (!areCollectionsReady || routeType === NavigationRouteType.ADMIN) {
      setAlwaysIncludePublicRecipesDocs([]);
      return undefined;
    }

    if (shouldUsePublicRecipes) {
      setAlwaysIncludePublicRecipesDocs([]);
      return undefined;
    }

    const ids = Array.isArray(coachConfig?.alwaysIncludeRecipes) ? coachConfig.alwaysIncludeRecipes : [];
    if (ids.length === 0) {
      setAlwaysIncludePublicRecipesDocs([]);
      return undefined;
    }

    Recipe.getRecipesByIds(ids).then((docs) => {
      if (isComponentMountedRef.current) {
        setAlwaysIncludePublicRecipesDocs(docs);
      }
    });

    return undefined;
  }, [
    areCollectionsReady,
    coachConfig,
    isComponentMountedRef,
    routeType,
    shouldUsePublicRecipes,
  ]);

  // Init states that won't prevent the context to be ready
  useEffect(() => {
    const init = async () => {
      let recipes;
      let publicRecipes;
      let coachRecipes;
      switch (routeType) {
        case NavigationRouteType.ADMIN:
          recipes = await Recipe.getAllRecipes();
          break;
        case NavigationRouteType.COACH:
        default:
          // Retrieve the two collections here so that we can observe changes on them.
          if (shouldUsePublicRecipes) {
            publicRecipes = await Recipe.getPublicRecipes();
          }
          coachRecipes = await Recipe.getRecipesByCreator(coachId, initialValues.showArchivedRecipes);
          break;
      }

      if (isComponentMountedRef.current) {
        if (publicRecipes && coachRecipes) {
          setPublicRecipesCollection(publicRecipes);
          setCoachRecipesCollection(coachRecipes);
        } else if (coachRecipes) {
          setCoachRecipesCollection(coachRecipes);
        } else {
          setRecipesCollection(recipes);
        }
      }
    };

    init();
  }, [
    isComponentMountedRef,
    routeType,
    coachId,
    shouldUsePublicRecipes,
  ]);

  const getCoachFilteredRecipes = useCallback((shouldFilterArchivedRecipes) => {
    const publicRecipesDocs = shouldUsePublicRecipes
      ? publicRecipesCollection.docs
      : alwaysIncludePublicRecipesDocs;

    // Filter deleted public recipes
    let filteredPublicRecipes = publicRecipesDocs.filter(({ deleted }) => !deleted);
    // Filter coach archived public recipes
    if (coachConfig.archivedRecipes.length > 0 && shouldFilterArchivedRecipes) {
      filteredPublicRecipes = filteredPublicRecipes.filter((recipe) => (
        !coachConfig.archivedRecipes.includes(recipe.id)
      ));
    }
    // Filter archived coach recipes
    const filteredCoachRecipes = shouldFilterArchivedRecipes
      ? coachRecipesCollection.docs.filter(({ isArchived }) => !isArchived) : coachRecipesCollection.docs;

    // Replace the edited recipes. Only keep the edited one (hide the original public recipe)
    filteredCoachRecipes.forEach((coachRecipe) => {
      const index = filteredPublicRecipes.findIndex((publicRecipe) => publicRecipe.id === coachRecipe.originalRecipe);
      // If we found the original recipe, remove it from the list.
      if (index >= 0) {
        filteredPublicRecipes.splice(index, 1);
      }
    });
    return [...filteredCoachRecipes, ...filteredPublicRecipes];
  }, [
    publicRecipesCollection.docs,
    coachRecipesCollection.docs,
    alwaysIncludePublicRecipesDocs,
    coachConfig,
    shouldUsePublicRecipes,
  ]);

  useEffect(() => {
    const disposer = autorun(() => {
      if (routeType === NavigationRouteType.ADMIN) {
        // Filter deleted recipes
        const filteredRecipes = recipesCollection.docs.filter(({ deleted }) => !deleted);
        setRecipesDocs(filteredRecipes);
      } else {
        const recipeDocs = getCoachFilteredRecipes(!showArchivedRecipes);
        setRecipesDocs(recipeDocs);
      }
    });
    return disposer;
  }, [
    routeType,
    recipesCollection.docs,
    showArchivedRecipes,
    getCoachFilteredRecipes,
    shouldUsePublicRecipes,
  ]);

  useEffect(() => {
    const init = async () => {
      const [
        mealPlanViewsCol,
        publicMealPlanViewsCol,
        assignmentViewsCol,
        coachConfigDoc,
      ] = await Promise.all([
        MealPlanView.getMealPlanViewsByCoach(coachId),
        MealPlanView.getPublicMealPlanViews(),
        ClientMealAssignmentView.getActiveClientMealAssignmentViewsByCoach(coachId),
        CoachConfig.getCoachConfigByCoachId(coachId),
      ]);

      if (isComponentMountedRef.current) {
        setCoachMealPlanViewsCollection(mealPlanViewsCol);
        setPublicMealPlanViewsCollection(publicMealPlanViewsCol);
        setAssignmentViewsCollection(assignmentViewsCol);
        setCoachConfig(coachConfigDoc);
        setAreCollectionsReady(true);
      }
    };

    if (!areCollectionsReady && !isAdminGroupRoute) {
      init();
    }
  }, [
    areCollectionsReady,
    coachId,
    isComponentMountedRef,
    isAdminGroupRoute,
  ]);

  /*
    Takes the client-level view data and the one plan being judged: the plan carries
    everything that differs per product, the client carries the rest.
  */
  const getAssignmentStatus = useCallback((clientData, plan) => {
    const {
      totalDailyCalories,
      mealPlanNotificationsResumeDate,
    } = clientData;

    const {
      lastUpdated,
      assignmentId,
      breakEndDate,
      serviceStartAt,
    } = plan;

    // If the user is on break we will not add any action notification
    if (!!breakEndDate && moment(breakEndDate.toDate()).isAfter(moment())
      && (!serviceStartAt || moment(serviceStartAt.toDate()).isBefore(moment()))) {
      return MealPlanAssignmentStatus.USER_ON_BREAK;
    }

    // If the user has disabled the meal plan notifications, we will not add any action items
    if (!!mealPlanNotificationsResumeDate && moment(mealPlanNotificationsResumeDate.toDate()).isAfter(moment())) {
      return MealPlanAssignmentStatus.NOTIFICATIONS_DISABLED;
    }

    // If no meal plan is present and we have a caloric goal, a new assignment is required
    if (!assignmentId && !!totalDailyCalories) {
      return MealPlanAssignmentStatus.PENDING;
    }

    // If the refresh interval is set to never, we don't need to have a notification
    if (!!assignmentId && mealPlanConfig?.refreshInterval === RefreshInterval.NEVER) {
      return MealPlanAssignmentStatus.MANUALLY_REFRESHABLE;
    }

    // If the assignment has expired beyond the refresh interval time, a refresh is required
    if (!!lastUpdated
      && moment.utc().isAfter(
        moment.utc(lastUpdated.toDate()).add(
          DaysForRefreshInterval[mealPlanConfig?.refreshInterval] || DEFAULT_MEAL_REFRESH_DAYS,
          'days',
        ),
      )
    ) {
      return MealPlanAssignmentStatus.NEEDS_REFRESH;
    }

    return MealPlanAssignmentStatus.ACTIVE;
  }, [mealPlanConfig]);

  /*
    One row per plan, so a client on two products produces two rows. `id` stays the client id —
    the client info panel and both nutrition tabs look their client up by it — while the grid keys
    each row on `id` + `productId` via `getRowId`.
  */
  const getAssignmentsWithStatus = useCallback((assignments) => {
    const { isFutureStartNotificationEnabled } = coachConfig;

    // This will hold the info about the users that don't have an assignment.
    const usersWithoutAssignment = [];
    // This is the list of assignments that are already in the DB.
    const usersWithAssignment = [];

    assignments.forEach((assignmentDoc) => {
      // Apply the filter condition inside the loop
      const clientData = assignmentDoc.data ? assignmentDoc.data : assignmentDoc;
      // Split off so a row does not carry every sibling plan, copying the client document per plan.
      const { plans = {}, ...clientFields } = clientData;

      // A product the client no longer holds is not the coach's problem
      Object.entries(plans)
        .filter(([, plan]) => isPlanLive(plan))
        .forEach(([productId, plan]) => {
          // Ignore an assignment if it's a future assignment and future notifications are disabled for the coach
          if (!isFutureStartNotificationEnabled
            && !!plan.serviceStartAt && moment(plan.serviceStartAt.toDate()).isAfter(moment())) {
            return;
          }

          const row = {
            ...clientFields,
            ...plan,
            productId,
            id: assignmentDoc.id,
            status: getAssignmentStatus(clientFields, plan),
          };

          const targetArray = plan.assignmentId ? usersWithAssignment : usersWithoutAssignment;
          targetArray.push(row);
        });
    });

    return [
      ...usersWithoutAssignment,
      ...usersWithAssignment,
    ];
  }, [
    coachConfig,
    getAssignmentStatus,
  ]);

  // Init States that need to be ready for the context to be ready
  useEffect(() => {
    const init = () => {
      autorun(() => {
        // Updates meal plan assignments status based on meal plan refresh interval
        const assignmentsInfo = getAssignmentsWithStatus(assignmentViewsCollection.docs);

        if (isComponentMountedRef.current) {
          setMealPlanAssignments(assignmentsInfo);
          setIsReady(true);
        }
      });
    };
    // Skip collection initialization if it's a support user
    if (isAdminGroupRoute) {
      setIsReady(true);
    } else if (!isReady && areCollectionsReady) {
      init();
    }
  }, [
    isReady,
    isComponentMountedRef,
    areCollectionsReady,
    isAdminGroupRoute,
    assignmentViewsCollection,
    getAssignmentsWithStatus,
  ]);

  // This useEffect updates assignment statuses when the refresh interval or future start notification settings changes
  useEffect(() => {
    // Assignment collection will not have been initialized if it's a support user
    if (isReady && !isAdminGroupRoute && assignmentViewsCollection) {
      // Re-derived from the view documents, never from the previous result: the rows are already
      // flattened out of the `plans` map, so feeding them back in would empty the table.
      setMealPlanAssignments(getAssignmentsWithStatus(assignmentViewsCollection.docs));
    }
  }, [
    isReady,
    isAdminGroupRoute,
    mealPlanConfig,
    getAssignmentsWithStatus,
    coachConfig,
    assignmentViewsCollection,
  ]);

  // Filter meal plan assignments based on selected pod
  useEffect(() => {
    const disposer = autorun(() => {
      if (mealPlanAssignments?.length) {
        const filtered = getFilteredDocs(mealPlanAssignments, 'userId');
        setFilteredMealPlanAssignments(filtered);
      } else {
        setFilteredMealPlanAssignments([]);
      }
    });

    return () => disposer();
  }, [mealPlanAssignments, getFilteredDocs]);

  // Update the meal plan status count based on pod-filtered assignments
  useEffect(() => {
    const assignmentHighlights = { ...initialValues.mealPlansStatusCount };

    if (filteredMealPlanAssignments?.length) {
      filteredMealPlanAssignments.forEach((assignmentDoc) => {
        const { status } = assignmentDoc;

        // Only update assignmentHighlights if the status is defined
        if (status) {
          assignmentHighlights[status] += 1;
        }
      });
    }

    setMealPlansStatusCount(assignmentHighlights);
  }, [filteredMealPlanAssignments]);

  // Root write on purpose — coach-own-doc field, paired with the root read above.
  const saveDefaultMealPlan = useCallback((newMealPlanId) => (
    coachUserDoc.updateFields({
      defaultMealPlan: newMealPlanId,
    })
  ), [coachUserDoc]);

  const saveMealPlanConfig = useCallback((config) => {
    const newConfigValue = {
      ...mealPlanConfig,
      ...config,
    };
    coachDoc.updateFields({
      mealPlanConfig: newConfigValue,
    });
  }, [
    mealPlanConfig,
    coachDoc,
  ]);

  const refetchMealPlanViews = useCallback(async (fetchArchivedPlans = false) => {
    setIsLoading(true);
    setShowArchivedMealPlans(fetchArchivedPlans);
    const mealPlanViews = await MealPlanView.getMealPlanViewsByCoach(coachId, fetchArchivedPlans);
    if (isComponentMountedRef.current) {
      setCoachMealPlanViewsCollection(mealPlanViews);
      setIsLoading(false);
    }
  }, [
    coachId,
    isComponentMountedRef,
  ]);

  const onChangeArchiveCheckBox = useCallback(async (fetchArchivedRecipes = false) => {
    if (!isArchivedRecipesLoaded) {
      setIsLoading(true);
      const coachRecipesCol = await Recipe.getRecipesByCreator(coachId, fetchArchivedRecipes);

      if (isComponentMountedRef.current) {
        setIsLoading(false);
        setCoachRecipesCollection(coachRecipesCol);
        setIsArchivedRecipesLoaded(true);
      }
    }
    setShowArchivedRecipes(fetchArchivedRecipes);
  }, [
    coachId,
    isComponentMountedRef,
    isArchivedRecipesLoaded,
  ]);

  // Compute combined meal plans when collections change
  useEffect(() => {
    if (!areCollectionsReady) {
      return undefined;
    }

    const disposer = autorun(() => {
      setIsLoading(true);

      const coachMealPlanViewsDocs = coachMealPlanViewsCollection?.hasDocs
        ? coachMealPlanViewsCollection.docs.slice() : [];
      const publicMealPlanViewsDocs = publicMealPlanViewsCollection?.hasDocs
        ? publicMealPlanViewsCollection.docs.slice() : [];

      const combinedMealPlans = MealPlanView.combineCoachAndPublic(coachMealPlanViewsDocs, publicMealPlanViewsDocs);

      if (isComponentMountedRef.current) {
        setComnbinedMealPlanViews(combinedMealPlans);
        setIsLoading(false);
      }
    });

    return disposer;
  }, [
    areCollectionsReady,
    isAdminGroupRoute,
    isComponentMountedRef,
    coachMealPlanViewsCollection,
    publicMealPlanViewsCollection,
  ]);

  const loadCanonicalIngredients = useCallback(async () => {
    if (canonicalIngredients) return;
    setIsCanonicalIngredientsLoading(true);
    try {
      const docs = await Ingredient.getAllIngredients();
      if (isComponentMountedRef.current) {
        setCanonicalIngredients(docs);
      }
    } finally {
      if (isComponentMountedRef.current) {
        setIsCanonicalIngredientsLoading(false);
      }
    }
  }, [canonicalIngredients, isComponentMountedRef]);

  const contextValue = useMemo(() => ({
    isReady,
    isLoading,
    recipesDocs,
    selectedRecipe,
    onSelectRecipe: setSelectedRecipe,
    mealPlanAssignments,
    automationTemplate,
    setAutomationTemplate,
    coachMealPlanViewsCollection,
    publicMealPlanViewsCollection,
    comnbinedMealPlanViews,
    defaultMealPlan,
    mealPlanConfig,
    mealPlansStatusCount,
    saveMealPlanConfig,
    saveDefaultMealPlan,
    refetchMealPlanViews,
    showArchivedMealPlans,
    showArchivedRecipes,
    onChangeArchiveCheckBox,
    coachRecipesCollection,
    publicRecipesCollection,
    getCoachFilteredRecipes,
    coachConfig,
    canonicalIngredients,
    isCanonicalIngredientsLoading,
    loadCanonicalIngredients,
  }), [
    isReady,
    isLoading,
    recipesDocs,
    selectedRecipe,
    mealPlanAssignments,
    automationTemplate,
    setAutomationTemplate,
    coachMealPlanViewsCollection,
    publicMealPlanViewsCollection,
    comnbinedMealPlanViews,
    defaultMealPlan,
    mealPlanConfig,
    mealPlansStatusCount,
    saveMealPlanConfig,
    saveDefaultMealPlan,
    refetchMealPlanViews,
    showArchivedMealPlans,
    showArchivedRecipes,
    onChangeArchiveCheckBox,
    coachRecipesCollection,
    publicRecipesCollection,
    getCoachFilteredRecipes,
    coachConfig,
    canonicalIngredients,
    isCanonicalIngredientsLoading,
    loadCanonicalIngredients,
  ]);

  return (
    <MealPlanContext.Provider value={contextValue}>
      {children}
    </MealPlanContext.Provider>
  );
};

MealPlanContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(MealPlanContextProvider);
